// import '../App.css';
import '../css/dashboard.css';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { FaEdit, FaFileAlt, FaCogs, FaChartBar, FaUserFriends, FaLock, FaTrash, FaMagic, FaCode } from "react-icons/fa";
import { toast } from 'react-hot-toast';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Legend, ResponsiveContainer,
} from 'recharts';
import { secureStorage } from "../SecureStorage";
import { Copy, ClipboardCopy, Clipboard } from "lucide-react";


function Dashboard({ orgId }) {
  const [indexedFiles, setIndexedFiles] = useState([]);
  const [buildLoading, setBuildLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [socials, setSocials] = useState({
    website: '',
    twitter: '',
    instagram: '',
    tiktok: '',
    whatsapp: '',
    contact_email: '',
    phone: ''
  });

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const chatEndRef = useRef(null);

  const [dashboardMessage, setDashboardMessage] = useState("");
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [bio, setBio] = useState({
    company_name: '',
    company_handle: '',
    greeting_message: ''
  });
  const [showBioModal, setShowBioModal] = useState(false);

  const [sessionId] = useState(() => {
    const existing = secureStorage.getItem('chat_session_id');
    if (existing) return existing;
  
    const newId = crypto.randomUUID();
    secureStorage.setItem('chat_session_id', newId);
    return newId;
  });

  const [orgStatus, setOrgStatus] = useState(null);      // "active" | "inactive"
  const [onTrial, setOnTrial] = useState(false);         // boolean
  const [trialDaysLeft, setTrialDaysLeft] = useState(0); // integer
  const [companyHandle, setCompanyHandle] = useState('');

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateInput, setGenerateInput] = useState({
    name: "",
    description: "",
    services: "",
    target: "",
    pricing: "",
    faqs: ""
  });
  const [genLoading, setGenLoading] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#4a3aff"); // default widget color

  {/* Color Picker */}
  <div style={{ margin: "12px 0" }}>
    <label htmlFor="widget-color">Widget Color: </label>
    <input
      type="color"
      id="widget-color"
      value={selectedColor}
      onChange={(e) => setSelectedColor(e.target.value)}
      style={{ width: "50px", height: "30px", border: "none", cursor: "pointer", marginLeft: "8px" }}
    />
  </div>



  useEffect(() => {
    if (orgId) {
      fetchOrganizationInfo();
      fetchIndexedFiles();
    }
  }, [orgId]); /* [orgId, showDocModal]); */

  // 2️⃣ Fetch org status after companyHandle is set
  // useEffect(() => {
  //   if (companyHandle) {
  //     fetchOrganizationStatus();
  //   }
  // }, [companyHandle]);


  useEffect(() => {
    const fetchBio = async () => {
      try {
        // Retrieve the token from secureStorage
        const token = secureStorage.getItem('auth_token');
        if (!token) {
          console.error("No auth token found");
          return;
        }
  
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/company/bio`, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });
  
        // if (!res.ok) {
        //   throw new Error(`Failed to fetch bio: ${res.status}`);
        // }
  
        // const data = await res.json();
        setBio({
          company_name: res.data.company_name,
          company_handle: res.data.company_handle,
          greeting_message: res.data.greeting_message || "",
        });
      } catch (err) {
        console.error("Error fetching organization bio:", err);
      }
    };
  
    fetchBio();
  }, []);
  

  const fetchIndexedFiles = async () => {
    try {
      
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/list_documents/?org_id=${orgId}`);
      setIndexedFiles(res.data.documents || []);
    } catch (err) {
      console.error('Error fetching indexed documents:', err);
    }finally {
      hideModal();
    }
  };

  // open modal (and then fetch docs)
  const handleOpenDocModal = async () => {
    showModal("Loading Documents... Please wait.");
    setShowDocModal(true);

    try {
      await fetchIndexedFiles();
    } catch (error) {
      console.error("Error fetching indexed files:", error);
    } finally {
      hideModal();
    }
  };

  // const [companyHandle, setCompanyHandle] = useState('');

  const fetchOrganizationInfo = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/organization_by_id/${orgId}`);
      setLogoUrl(res.data.logo_url || '');
      const handle = res.data.handle || '';
      setCompanyHandle(res.data.handle || '');
      console.log("handle:", handle);
      setSocials({
        website: res.data.website || '',
        twitter: res.data.twitter || '',
        instagram: res.data.instagram || '',
        tiktok: res.data.tiktok || '',
        whatsapp: res.data.whatsapp || '',
        contact_email: res.data.contact_email || '',
        phone: res.data.phone || ''
      });

      // ✅ Fetch org status immediately using handle from API
      if (handle) {
        fetchOrganizationStatus(handle);
      }

    } catch (err) {
      console.error('Error fetching org info:', err);
    }
  };

  const fetchOrganizationStatus = async (handle) => {
    // if (!companyHandle) return; // safety check
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/org-status/${handle}`);
      setOrgStatus(res.data.status);       // active | inactive
      setOnTrial(res.data.on_trial);       // boolean
      setTrialDaysLeft(res.data.days_left); // integer
    } catch (err) {
      console.error('Error fetching org status:', err);
    }
  };
  

  const handleBuildIndex = async () => {
    showModal("Building index... Please wait.");
    setBuildLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/build_index/?org_id=${orgId}`);
      toast.success(res.data.message || 'File uploaded successfully.');
      // alert(res.data.message || 'Index built successfully.');
      fetchIndexedFiles();
      setChatHistory([]);
    } catch (error) {
      console.error('Build index error:', error);
      // alert(error.response?.data?.detail || 'Failed to build index.');
      toast.error(error.response?.data?.detail || 'Build index error.');
    } finally {
      setBuildLoading(false);
      hideModal();
    }
  };

  const handleDeleteFile = async (filename) => {
    // if (!window.confirm(`Delete ${filename}?`)) return; # Makes browser show deletion confirmation
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/delete_document/?org_id=${orgId}&filename=${encodeURIComponent(filename)}`);
      // alert(`${filename} deleted.`);
      fetchIndexedFiles();
    } catch (error) {
      console.error('Delete error:', error);
      // alert(error.response?.data?.detail || 'Delete failed.');
    }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;

    const userMessage = chatInput.trim();
    setChatHistory((prev) => [...prev, { role: 'user', text: userMessage }]);
    setChatInput('');
    setChatLoading(true);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/ask/`, {
        question: userMessage,
        company_id: orgId,
        session_id: sessionId,
      });
      setChatHistory((prev) => [...prev, { role: 'bot', text: res.data.answer || 'No reply.' }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatHistory((prev) => [...prev, { role: 'bot', text: 'Error getting response.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', logoFile);

    try {
      showModal("Uploading Logo... Please wait.");
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/upload_logo/${orgId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // alert(res.data.message || 'Logo uploaded.');
      setLogoUrl(res.data.logo_url);
    } catch (error) {
      console.error('Logo upload error:', error);
      // alert(error.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploadingLogo(false);
      hideModal();
    }
  };

  const handleDocumentUpload = async () => {
    if (!selectedFile) return;
    const formData = new FormData();

    const UPLOAD_MAX_FILE_SIZE_MB = 25; // File Limit upload
    const UPLOAD_MAX_FILE_SIZE = UPLOAD_MAX_FILE_SIZE_MB * 1024 * 1024;

    if (selectedFile.size > UPLOAD_MAX_FILE_SIZE) {
      toast.error(`File too large. Max allowed size is ${UPLOAD_MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    formData.append('file', selectedFile);

    showModal("Uploading and indexing document...");

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/upload_file/?org_id=${orgId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // setUploadProgress(percent);
        }
      });

      toast.success(res.data.message || 'File uploaded successfully.', {duration: 8000,}); // 8 secs duration for toast to show
      // toast.success(res.data.message || 'File uploaded successfully.');
      setSelectedFile(null);
      // setUploadProgress(0);
      fetchIndexedFiles();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed.');
      // alert(error.response?.data?.detail || 'Upload failed.');
      // setUploadProgress(0);
    } finally {
      hideModal();
    }
  };

  const handleSaveBio = async () => {
    try {
      showModal("Updating bio... Please wait."); // Show loader/modal
  
      const payload = {};
  
      if (bio.company_name.trim()) payload.name = bio.company_name.trim();
      if (bio.company_handle.trim()) payload.handle = bio.company_handle.trim();
      if (bio.greeting_message !== null) payload.greeting_message = bio.greeting_message;
  
      console.log("Sending payload:", payload); // Debugging
  
      // Get the JWT token from secureStorage
      const token = secureStorage.getItem('auth_token');
      if (!token) throw new Error("No auth token found. Please log in again.");
  
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/company/bio`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`, // Use token instead of x-org-id
        },
        body: JSON.stringify(payload),
      });
  
      // if (!response.ok) throw new Error("Failed to save bio");

      if (!response.ok) {
        const errorData = await response.json();
  
        // Show backend message directly
        if (errorData.detail) {
          toast.error(errorData.detail);
        } else {
          toast.error("Failed to update bio.");
        }
  
        return; // Stop execution here
      }
  
  
      toast.success("Bio updated successfully!");
      setShowBioModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Error saving bio.");
    } finally {
      hideModal(); // Always hide loader
    }
  };
  
  
  const handleOpenSocials = async () => {
    try {
      showModal("Loading Socials... Please wait."); 
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/organization_by_id/${orgId}`); // Use the org handle or orgId route
      setSocials({
        website: res.data.website || '',
        twitter: res.data.twitter || '',
        instagram: res.data.instagram || '',
        tiktok: res.data.tiktok || '',
        whatsapp: res.data.whatsapp || '',
        contact_email: res.data.contact_email || '',
        phone: res.data.phone || '',
      });
      setShowSocialModal(true); // Open modal after data is ready
    } catch (err) {
      alert("Failed to load socials.");
      console.error(err);
    }finally {
      hideModal(); // 🔥 hide loader no matter what
    }
  };
  

  const handleSaveSocials = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/update_socials/${orgId}`, socials);
      // alert("Social links saved.");
      setShowSocialModal(false);
      toast.success("Socials updated successfully!");
    } catch (err) {
      // alert("Failed to save socials.");
      toast.success("Socials update failed!");
      console.error(err);
    }
  };

  const handleOpenAnalytics = async () => {
    try {
      showModal("Loading analytics... Please wait."); // 🔥 show loader
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/analytics/${orgId}?start_date=${startDate}&end_date=${endDate}`);
      const reviewsRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/organization/${orgId}/reviews?start_date=${startDate}&end_date=${endDate}`);
      
      setAnalytics(res.data);
      setReviews(reviewsRes.data.reviews); // Access the "reviews" field from response
      setAverageRating(reviewsRes.data.average_rating); 
      setShowAnalyticsModal(true);
    } catch (err) {
      alert("Failed to load analytics or reviews.");
      console.error(err);
    }finally {
      hideModal(); // 🔥 hide loader no matter what
    }
  };

  const showModal = (message = "Processing... Please wait.") => {
    setDashboardMessage(message);
    setDashboardLoading(true);
    const modal = document.getElementById("dashboardLoadingModal");
    const text = document.getElementById("dashboard-modal-message");
    if (modal && text) {
      modal.style.display = "flex";
      text.innerText = message;
    }
  };
  
  const hideModal = () => {
    setDashboardLoading(false);
    const modal = document.getElementById("dashboardLoadingModal");
    if (modal) modal.style.display = "none";
  };

  // Add this with your other useState hooks
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Please fill in both fields.");
      return;
    }
  
    try {
      const token = secureStorage.getItem("auth_token");
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/change_password/`,
        null,
        {
          params: { current_password: currentPassword, new_password: newPassword },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(res.data.message);
      setShowChangePassword(false);
      setCurrentPassword("");
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };
  

  // Modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteAccount = async () => {
    try {
      const token = secureStorage.getItem("auth_token");
      const res = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/delete_account/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(res.data.message);
      // Log out user
      secureStorage.clear();
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    }
  };

  const handleGenerateDocument = async () => {
    setGenLoading(true);
  
    try {
      const token = secureStorage.getItem("auth_token");
  
      // Step 1 — Generate doc
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/generate-doc`,
        generateInput,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
  
      const { filename, content } = res.data;
  
      // Step 2 — Convert content → File object
      const txtBlob = new Blob([content], { type: "text/plain" });
      const fileToUpload = new File([txtBlob], filename, { type: "text/plain" });
  
      // Step 3 — Prepare multipart form for /upload_file
      const formData = new FormData();
      formData.append("file", fileToUpload);
  
      // Step 4 — Upload using your existing endpoint
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/upload_file/?org_id=${orgId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
  
      toast.success("Document generated and uploaded successfully!");
  
      // Reset modal input
      setShowGenerateModal(false);
      setGenerateInput({
        name: "",
        description: "",
        services: "",
        target: "",
        pricing: "",
        faqs: ""
      });
  
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to generate document");
    }
  
    setGenLoading(false);
  };

  const handleCopyEmbed = async () => {
    try {
      // Include widget_color in the payload
      const payload = { widget_color: selectedColor };
  
      // Call your existing handleSaveBio endpoint
      const token = secureStorage.getItem('auth_token');
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/company/bio`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
  
      // Copy embed code to clipboard
      navigator.clipboard.writeText(
        `<script src="https://www.querybox.app/widget.js" data-org="${orgId}" data-color="${selectedColor}"></script>`
      );
  
      toast.success("Embed code copied and color saved!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save color or copy embed code.");
    }
  };
  

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className="dashboard-sidebar">
        <div className="logo-section">
          <h4>Company Logo</h4>
          {logoUrl && <img src={logoUrl} alt="Logo" className="logo-img" />}
          <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} />
          <button className="upload-btn" onClick={handleLogoUpload} disabled={uploadingLogo || !logoFile}>
            {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
          </button>
        </div>
  
        <div className="upload-section">
          {/* <h4>Upload New Document</h4> */}
          <h4>Upload Your Business Document</h4>
          <p>Upload a file to train your company chatbot.</p>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={(e) => setSelectedFile(e.target.files[0])}
          />
          <button className="upload-btn" onClick={handleDocumentUpload} disabled={!selectedFile}>
            Upload File
          </button>
          {uploadProgress > 0 && (
            <div className="progress-bar-container">
              <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>{uploadProgress}%</div>
            </div>
          )}

          {/* Separator */}
          <div className="separator" style={{ textAlign: "center", margin: "20px 0" }}>
            <span>or</span>
          </div>

          {/* Generate Section */}
          <h4>No Document? Create One Automatically</h4>
          <p>Fill in a few details to generate a business profile for your chatbot.</p>
          <button
            className="primary-btn"
            onClick={() => setShowGenerateModal(true)}
          >
            <FaMagic /> Create Business Profile
          </button>
          
        </div>
        
        <button className="primary-btn" onClick={() => setShowBioModal(true)}>
          <FaEdit /> Edit Bio
        </button>

        <button className="primary-btn" onClick={() => setShowEmbedModal(true)}>
          <FaCode /> Embed on Website
        </button>

        <button className="primary-btn" onClick={handleOpenDocModal}>
          <FaFileAlt /> View Documents
        </button>

        <button className="primary-btn" onClick={handleBuildIndex} disabled={buildLoading}>
          <FaCogs /> {buildLoading ? 'Building...' : 'Build Knowledge'}
        </button>

        <button className="primary-btn" onClick={() => handleOpenSocials()}>
          <FaUserFriends /> Socials
        </button>

        <button className="primary-btn" onClick={handleOpenAnalytics}>
          <FaChartBar /> Analytics
        </button>

        <button className="primary-btn" onClick={() => setShowChangePassword(true)}>
          <FaLock /> Change Password
        </button>

        <button className="primary-btn danger-btn" onClick={() => setShowDeleteConfirm(true)}>
          <FaTrash /> Delete Account
        </button>
  

      </div>
  
      {/* Chat Area */}
      <div className="dashboard-main">
        {/* --- Trial Banner --- */}
        {onTrial && trialDaysLeft > 0 && (
          <div className="trial-banner">
            ⏳ Your free trial ends in {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'}.
            After expiry, customers will no longer be able to access your bot. 
            <button
              className="upgrade-btn"
              onClick={() => window.location.href = '/billing'}
            >
              Upgrade Now
            </button>
          </div>
        )}
      <h3>
          Test Your Company Bot{" "}
          {companyHandle && (
            <span className="share-link">
              | Share Your Bot:{" "}
              <a
                href={`https://${companyHandle}.querybox.app`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {companyHandle}.querybox.app
              </a>
              <button
                className="copy-btn"
                onClick={() =>
                {
                  navigator.clipboard.writeText(
                    `https://${companyHandle}.querybox.app`
                  );
                  toast.success("Copied!", {
                    duration: 2000,
                    style: {
                      background: "#333",
                      color: "#fff",
                      fontSize: "14px",
                    },
                  });
                  
                  // toast.success("Copied!");
                }}
              >
                <Clipboard size={12} />
                {/* 📋 */}
              </button>
            </span>
          )}
        </h3>
        {/* <h3>Test Your Company Bot</h3> */}
        <div className="chat-box">
          {chatHistory.length === 0 && <p className="chat-placeholder">Start chatting...</p>}
          {chatHistory.map((msg, idx) => (
            <div key={idx} className={`chat-msg ${msg.role}`}>
              {msg.role === 'bot' ? <ReactMarkdown>{msg.text}</ReactMarkdown> : <span>{msg.text}</span>}
            </div>
          ))}
          {chatLoading && <p>Bot is typing...</p>}
          <div ref={chatEndRef} />
        </div>
  
        <div className="chat-input-container">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder="Ask a question..."
            onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
          />
          <button onClick={handleSendChat} disabled={chatLoading}>Send</button>
        </div>
      </div>
  
      {/* Bio Modal */}
      {showBioModal && (
        <div className="modal-overlay" onClick={() => setShowBioModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <h4>Edit Company Bio</h4>
              <span className="modal-close" onClick={() => setShowBioModal(false)}>×</span>
            </div>
  
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveBio();
              }}
            >
              <label>
                Company Name:
                <input
                  type="text"
                  value={bio.company_name}
                  onChange={(e) => setBio({ ...bio, company_name: e.target.value })}
                  required
                />
              </label>
              <label>
                Company Handle:
                <input
                  type="text"
                  value={bio.company_handle}
                  onChange={(e) => setBio({ ...bio, company_handle: e.target.value })}
                  required
                />
              </label>
              <label>
                Greeting Message:
                <textarea
                  value={bio.greeting_message}
                  onChange={(e) => setBio({ ...bio, greeting_message: e.target.value })}
                />
              </label>
              <button type="submit" className="primary-btn">Save</button>
            </form>
          </div>
        </div>
      )}
  
      {/* Document Modal */}
      {showDocModal && (
        <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <h4>Indexed Documents</h4>
              <span className="modal-close" onClick={() => setShowDocModal(false)}>×</span>
            </div>
  
            {indexedFiles.length === 0 ? (
              <p>No files indexed yet.</p>
            ) : (
              <ul className="file-list">
                {indexedFiles.map((file, idx) => (
                  <li key={idx} className="file-item">
                    {file}
                    <button onClick={() => handleDeleteFile(file)} className="delete-btn">Delete</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
  
      {/* Socials Modal */}
      {showSocialModal && (
        <div className="modal-overlay" onClick={() => setShowSocialModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <h4>Edit Social Links</h4>
              <span className="modal-close" onClick={() => setShowSocialModal(false)}>×</span>
            </div>
  
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveSocials();
              }}
            >
              <label>
                Website:
                <input
                  type="text"
                  value={socials.website}
                  onChange={(e) => setSocials({ ...socials, website: e.target.value })}
                />
              </label>
              <label>
                Twitter:
                <div className="input-prefix">
                  <span>https://x.com/</span>
                  <input
                    type="text"
                    placeholder="justinbieber"
                    value={socials.twitter}
                    onChange={(e) => setSocials({ ...socials, twitter: e.target.value })}
                  />
                </div>
              </label>
              {/* <label>
                Twitter:
                <input
                  type="text"
                  value={socials.twitter}
                  onChange={(e) => setSocials({ ...socials, twitter: e.target.value })}
                />
              </label> */}
              <label>
                Instagram:
                <div className="input-prefix">
                  <span>https://instagram.com/</span>
                  <input
                    type="text"
                    placeholder="alikodangote"
                    value={socials.instagram}
                    onChange={(e) => setSocials({ ...socials, instagram: e.target.value })}
                  />
                </div>
              </label>
              {/* <label>
                Instagram:
                <input
                  type="text"
                  value={socials.instagram}
                  onChange={(e) => setSocials({ ...socials, instagram: e.target.value })}
                />
              </label> */}
              <label>
                TikTok:
                <div className="input-prefix">
                  <span>https://tiktok.com/@</span>
                  <input
                    type="text"
                    placeholder="elonmusk"
                    value={socials.tiktok}
                    onChange={(e) => setSocials({ ...socials, tiktok: e.target.value })}
                  />
                </div>
              </label>
              {/* <label>
                Tiktok:
                <input
                  type="text"
                  value={socials.tiktok}
                  onChange={(e) => setSocials({ ...socials, tiktok: e.target.value })}
                />
              </label> */}
              <label>
                WhatsApp:
                <input
                  type="text"
                  value={socials.whatsapp}
                  onChange={(e) => setSocials({ ...socials, whatsapp: e.target.value })}
                />
              </label>
              <label>
                Contact Email:
                <input
                  type="email"
                  value={socials.contact_email}
                  onChange={(e) => setSocials({ ...socials, contact_email: e.target.value })}
                />
              </label>
              <label>
                Phone:
                <input
                  type="text"
                  value={socials.phone}
                  onChange={(e) => setSocials({ ...socials, phone: e.target.value })}
                />
              </label>
              <button type="submit" className="primary-btn">Save</button>
            </form>
          </div>
        </div>
      )}

      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => setShowGenerateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <h4>Create a Business Document</h4>
              <span className="modal-close" onClick={() => setShowGenerateModal(false)}>×</span>
            </div>

            <p>Fill in the details below and we’ll automatically create a clean business profile for you.</p>

            <form
              className="generate-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleGenerateDocument();
              }}
            >
               <label>
                Business/Company Name:
                <textarea
                  value={generateInput.name}
                  onChange={(e) =>
                    setGenerateInput({ ...generateInput, name: e.target.value })
                  }
                />
              </label>

              <label>
                Describe Your Business:
                <textarea
                  value={generateInput.description}
                  onChange={(e) =>
                    setGenerateInput({ ...generateInput, description: e.target.value })
                  }
                  required
                />
              </label>

              <label>
                Services You Offer:
                <textarea
                  value={generateInput.services}
                  onChange={(e) =>
                    setGenerateInput({ ...generateInput, services: e.target.value })
                  }
                />
              </label>

              <label>
                Target Customers:
                <textarea
                  value={generateInput.target}
                  onChange={(e) =>
                    setGenerateInput({ ...generateInput, target: e.target.value })
                  }
                />
              </label>

              <label>
                Pricing (optional):
                <textarea
                  value={generateInput.pricing}
                  onChange={(e) =>
                    setGenerateInput({ ...generateInput, pricing: e.target.value })
                  }
                />
              </label>

              <label>
                Frequently Asked Questions (optional):
                <textarea
                  value={generateInput.faqs}
                  onChange={(e) =>
                    setGenerateInput({ ...generateInput, faqs: e.target.value })
                  }
                />
              </label>

              <button type="submit" className="primary-btn" disabled={genLoading}>
                {genLoading ? "Generating..." : "Generate & Upload"}
              </button>
            </form>
          </div>
        </div>
      )}

  
      {/* Analytics Modal with Date Filters and Charts */}
      {showAnalyticsModal && (
        <div className="modal-overlay" onClick={() => setShowAnalyticsModal(false)}>
          <div className="modal-content analytics-modal" onClick={(e) => e.stopPropagation()}>
            <div 
              style={{ 
                position: 'relative', 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start" 
              }}
            >
              <div>
                <h4 style={{ margin: 0 }}>Analytics & Reviews</h4>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/download-chats/${orgId}`,
                        { responseType: "blob" } // important for binary file
                      );

                      // Create downloadable link
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement("a");
                      link.href = url;
                      link.setAttribute("download", `org_${orgId}_chats.xlsx`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (err) {
                      alert("Failed to download chats.");
                      console.error(err);
                    }
                  }}
                  className="download-chat-btn"
                >
                  Export Chat History
                </button>
              </div>

              {/* Close Button */}
              <span 
                className="modal-close" 
                onClick={() => setShowAnalyticsModal(false)} 
                style={{ cursor: "pointer", fontSize: "20px" }}
              >
                ×
              </span>
            </div>


            {/* <div style={{ position: 'relative' }}>
              <h4>Analytics & Reviews</h4>
              
              <span className="modal-close" onClick={() => setShowAnalyticsModal(false)}>×</span>
            </div> */}
  
            <label>Start Date: <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
            <label>End Date: <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></label>
            <button onClick={handleOpenAnalytics}>Filter</button>
  
            {analytics ? (
              <>
                <div className="analytics-data">
                  <p>Total Questions: {analytics.total_questions_asked}</p>
                  <p>Unique Sessions: {analytics.unique_sessions}</p>
                </div>
  
                <h5>Daily Queries</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.daily_data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="queries" stroke="#8884d8" />
                  </LineChart>
                </ResponsiveContainer>
  
              </>
            ) : (
              <p>Loading analytics...</p>
            )}
  
            <h5>Customer Reviews</h5>
            <p>Average Rating: {averageRating}</p>
            {reviews.length === 0 ? (
              <p>No reviews yet.</p>
            ) : (
              <ul className="reviews-list">
                {reviews.map((rev, idx) => (
                  <li key={idx}>
                    <strong>Rating:</strong> {rev.rating} <br />
                    <strong>Comment:</strong> {rev.comment || "No comment"} <br />
                    <small>{new Date(rev.created_at).toLocaleString()}</small>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {dashboardLoading && (
        <div id="dashboardLoadingModal" className="modal-index-overlay">
          <div className="modal-index-content">
            <div className="spinner"></div>
            <p id="dashboard-modal-message">{dashboardMessage}</p>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h3>Delete Account</h3>
            <p>Are you sure you want to delete your account? This action cannot be undone.</p>
            <div className="confirm-actions">
              <button
                className="primary-btn danger-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  handleDeleteAccount();
                }}
              >
                Yes, Delete
              </button>
              <button
                className="primary-btn"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmbedModal && (
        <div 
          className="modal-overlay" 
          onClick={() => setShowEmbedModal(false)}
        >
          <div 
            className="modal-content" 
            style={{ maxWidth: "500px" }} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div 
              style={{ 
                position: "relative", 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start"
              }}
            >
              <h4 style={{ margin: 0 }}>Embed Chatbot</h4>

              <span 
                className="modal-close"
                onClick={() => setShowEmbedModal(false)}
                style={{ cursor: "pointer", fontSize: "20px" }}
              >
                ×
              </span>
            </div>

            {/* Instructions */}
            <p style={{ marginTop: "10px" }}>
              Copy this code and paste it before the closing <code>&lt;/body&gt;</code> tag of your website.
            </p>

            {/* Color Picker */}
            <div style={{ margin: "12px 0", display: "flex", alignItems: "center", gap: "12px" }}>
              <label htmlFor="widget-color" style={{ fontWeight: 500 }}>Widget Color:</label>
              <input
                type="color"
                id="widget-color"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                style={{ width: "50px", height: "30px", border: "none", cursor: "pointer" }}
              />

              <button
                className="primary-btn"
                onClick={async () => {
                  try {
                    // Save only the widget_color to backend using existing bio endpoint
                    const payload = { widget_color: selectedColor };
                    const token = secureStorage.getItem('auth_token');
                    await fetch(`${process.env.REACT_APP_API_BASE_URL}/company/bio`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                      },
                      body: JSON.stringify(payload),
                    });

                    toast.success("Widget color saved!");
                  } catch (err) {
                    console.error(err);
                    toast.error("Error saving widget color");
                  }
                }}
              >
                Save Color
              </button>
            </div>

            {/* Embed Code Box */}
            <textarea
              readOnly
              className="embed-code-box"
              value={`<script src="https://www.querybox.app/widget.js" data-org="${orgId}" data-color="${selectedColor}"></script>`}
              style={{
                width: "100%",
                height: "120px",
                marginTop: "10px",
                padding: "12px",
                borderRadius: "8px",
                fontFamily: "monospace",
                fontSize: "14px",
                outline: "none",
                resize: "none",
                border: "1px solid #ccc",
                boxSizing: "border-box"
              }}
            />

            {/* Footer Actions */}
            <div 
              style={{
                marginTop: "18px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px"
              }}
            >
              <button
                className="primary-btn"
                onClick={async () => {
                  try {
                    // Save widget color while copying embed code
                    const payload = { widget_color: selectedColor };
                    const token = secureStorage.getItem('auth_token');
                    await fetch(`${process.env.REACT_APP_API_BASE_URL}/company/bio`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                      },
                      body: JSON.stringify(payload),
                    });

                    // Copy embed code to clipboard
                    navigator.clipboard.writeText(
                      `<script src="https://www.querybox.app/widget.js" data-org="${orgId}" data-color="${selectedColor}"></script>`
                    );

                    toast.success("Embed code copied and color saved!");
                  } catch (err) {
                    console.error(err);
                    toast.error("Failed to save color or copy embed code");
                  }
                }}
              >
                Copy Code
              </button>

              <button
                className="primary-btn danger-btn"
                onClick={() => setShowEmbedModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}



      {showChangePassword && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h3>Change Password</h3>
            <p>Enter your current and new password below:</p>

            <input
              type="password"
              placeholder="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input-field"
              style={{ marginBottom: "0.75rem", width: "100%" }}
            />

            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input-field"
              style={{ marginBottom: "1.25rem", width: "100%" }}
            />

            <div className="confirm-actions">
              <button
                className="primary-btn"
                onClick={handleChangePassword}
              >
                Change
              </button>
              <button
                className="primary-btn danger-btn"
                onClick={() => setShowChangePassword(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}



    </div>
  );
  

}

export default Dashboard;

